import React from "react";
import AgTable from "../../components/AgTable";
import { AgColDef } from "../../components/AgTable/types";
import { useStoreState } from "../../hooks/ep";
import { getEnrollmentBuzzUrl } from "../../entities/ecp/ecp-enrollment-entity";
import _ from "lodash";
import {
  getStudentBuzzUrl,
  getStudentGeniusUrl,
} from "../../entities/ecp/ecp-student-entity";
import { useEnsureData } from "../../helpers/use-ensure-data";
import { Spinner } from "@blueprintjs/core";

export function EnrollmentActivitySummaryTable({
  fullScreenEnabled = true,
  customRibbonElements = [],
  ...restProps
}): JSX.Element {
  const loading = _.max([useEnsureData("enrollmentActivitySummary")]);

  return loading ? (
    <Spinner />
  ) : (
    <EnrollmentActivitySummaryContent
      fullScreenEnabled={fullScreenEnabled}
      customRibbonLeftElements={customRibbonElements}
      {...restProps}
    />
  );
}

function EnrollmentActivitySummaryContent({ ...restProps }): JSX.Element {
  const rowData = useStoreState((s) => s.enrollmentActivitySummary.initialData);

  const colDefs = useEnrollmentActivitySummaryColDefs();

  return (
    <AgTable
      tableName={"enrollment-activity-summary"}
      rowData={rowData}
      columnDefs={colDefs}
      ribbonTitle={"Enrollment Activity Summary (Last 7 days)"}
      {...restProps}
    />
  );
}

function useEnrollmentActivitySummaryColDefs(): AgColDef[] {
  return [
    {
      headerName: "Student Name",
      field: "customer_name",
      type: "textColumn",
      width: 150,
      suppressSizeToFit: true,
      pinned: "left",
      tooltipField: "customer_name",
    },
    {
      headerName: "Buzz Enr",
      field: "eLMSID",
      type: "linkedTextColumn",
      pinned: "left",
      __linkValueGetter: getEnrollmentBuzzUrl,
      __linkIsNotAnEmail: true,
    },
    {
      headerName: "Activity Start Date (UTC)",
      field: "ActivityStartDate",
      type: "utcTimestampColumn",
      pinned: "left",
      __timeFormat: "ddd, MMM D, YYYY HH:mm:ss",
    },
    {
      headerName: "Department",
      field: "DepartmentName",
      type: "categoryColumn",
    },
    {
      headerName: "Course",
      field: "CourseName",
      type: "textColumn",
      width: 225,
      suppressSizeToFit: true,
      tooltipField: "CourseName",
    },
    {
      headerName: "Activity Title",
      field: "ActivityTitle",
      type: "textColumn",
      width: 325,
      suppressSizeToFit: true,
      tooltipField: "ActivityTitle",
    },
    {
      headerName: "Activity Duration (s)",
      field: "ActivityDurationInSeconds",
      type: "integerColumn",
    },
    {
      headerName: "Activity Duration (h)",
      field: "activity_duration_in_hours",
      type: "floatColumn",
      __nDecimals: 3,
    },
    {
      headerName: "SoR",
      field: "sSchoolRecord",
      type: "booleanColumn",
    },
    {
      headerName: "Stu Grade Level",
      field: "sGradeLevel",
      type: "integerColumn",
      filter: "categoryFilterer",
      cellClass: "ag-numeric-cell",
      width: 130,
    },
    {
      headerName: "Country",
      field: "Country",
      type: "textColumn",
    },
    {
      headerName: "State",
      field: "State",
      type: "textColumn",
    },
    {
      headerName: "City",
      field: "City",
      type: "textColumn",
    },
    {
      headerName: "ZIP",
      field: "ZIP",
      type: "textColumn",
    },
    {
      headerName: "Buzz Stu",
      field: "student_lmsid",
      type: "linkedTextColumn",
      __linkValueGetter: getStudentBuzzUrl,
      __linkIsNotAnEmail: true,
    },
    {
      headerName: "Genius Stu",
      field: "StudentIndex",
      type: "linkedTextColumn",
      __linkValueGetter: getStudentGeniusUrl,
      __linkIsNotAnEmail: true,
      width: 97,
    },
    {
      headerName: "Genius Enrollment ID",
      field: "EnrollmentIndex",
      type: "integerIdentifierColumn",
    },
    {
      headerName: "Genius Course ID",
      field: "CourseIndex",
      type: "integerIdentifierColumn",
    },
    {
      headerName: "Activity ID",
      field: "ActivityID",
      type: "textColumn",
      width: 305,
      suppressSizeToFit: true,
      tooltipField: "ActivityID",
    },
  ];
}
